<template>
  <v-container fluid class="planned-service-index pa-0">
    <EntityIndex
        :loading="loading"
        :total-count="totalCount"
        :table-headers="tableHeaders"
        :table-rows="tableRows"
        :table-options.sync="tableOptions"
        :selected-rows.sync="selectedRows"
        :selected-rows-actions="selectedRowsActions"
        :no-data-text="$t('plannedService.calendarEvents.noEventsOnDateRange')"
        item-key="scheduledPlannedService.plannedServiceInstanceId">

      <template v-slot:actions>
        <v-btn v-if="$featurePermissions.plannedServiceCRUD"
               color="primary"
               elevation="2"
               class="mb-2"
               @click="$refs.plannedServiceDialog.openCreateNewPlannedService()">
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>


      <template v-slot:filter>
        <entity-view-filter>
          <template v-slot:filter1>
            <date-range-picker
                v-model="dateRange"
                @change="fetch"/>
          </template>
          <template v-slot:filter2>
            <device-select-field
                v-model="filterDevice"
                :label="$t('plannedService.device')"
                @change="fetch"
                :show-select-dialog-button="true"/>
          </template>
          <template v-slot:filter3>
            <search-field
                v-model="filterText"
                hide-details="auto"
                :label="$t('plannedService.filterByText')"
                @change="fetch"/>
          </template>
        </entity-view-filter>
      </template>

      <template v-slot:item.name="{ item }">
        <router-link :to="item.routerLinkTo">{{ item.name }}</router-link>
      </template>
      <template v-slot:item.serviceManagers="{ item }">
        <plain-unordered-list :items="item.serviceManagers"/>
      </template>
      <template v-slot:item.authorUsers="{ item }">
        <plain-unordered-list :items="item.authorUsers"/>
      </template>


      <template v-slot:entityListItem="props">
        <scheduled-planned-service-list-item
            :item="props.item"
            :show-actions="true"
            :row-click-enabled="true"/>
      </template>

    </EntityIndex>


    <entity-confirm-delete-dialog
        ref="confirmDeletePlannedServiceInstanceDialog"
        :text="$t('plannedService.deletePlannedServiceInstanceConfirm')"
        :text-plural="$t('plannedService.deletePlannedServiceInstanceConfirmPlural')"

        deletePlannedServiceInstanceConfirmPlural
        @confirmedDelete="deletePlannedServiceInstances">
      <template v-slot:entityListItem="props">
        <scheduled-planned-service-list-item :item="props.item"/>
      </template>
    </entity-confirm-delete-dialog>

    <entity-confirm-delete-dialog
        ref="confirmDeletePlannedServiceDialog"
        :text="$t('plannedService.deleteRepeatingPlannedServiceConfirm')"
        @confirmedDelete="deletePlannedService">
      <template v-slot:entityListItem="props">
        <scheduled-planned-service-list-item :item="props.item"/>
      </template>
    </entity-confirm-delete-dialog>

    <planned-service-instance-move-to-date-dialog
        ref="moveToDateDialog"
        @done="fetch"/>

    <planned-service-dialog ref="plannedServiceDialog" @done="fetch"/>
    <service-dialog ref="serviceDialog" @done="fetch"/>
  </v-container>

</template>

<style scoped>

</style>

<script>
import EntityViewFilter from '@/components/entity-crud/EntityViewFilter';
import DateRangePicker from '@/components/inputs/DateRangePicker';
import SearchField from '@/components/inputs/SearchField';
import PlannedServiceService from '@/services/PlannedServiceService';
import i18n from '@/i18n';
import PlainUnorderedList from '@/components/layouts/PlainUnorderedList';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import ScheduledPlannedServiceListItem from './ScheduledPlannedServiceListItem';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import PlannedServiceInstanceMoveToDateDialog from '@/views/plannedService/PlannedServiceInstanceMoveToDateDialog';
import PlannedServiceDialog from '@/views/plannedService/PlannedServiceDialog';
import DeviceSelectField from '@/components/inputs/DeviceSelectField';
import ServiceDialog from '@/views/service/ServiceDialog';

export default {
  components: {
    ServiceDialog,
    DeviceSelectField,
    PlannedServiceDialog,
    PlannedServiceInstanceMoveToDateDialog,
    EntityConfirmDeleteDialog,
    ScheduledPlannedServiceListItem,
    EntityIndex,
    PlainUnorderedList,
    SearchField,
    EntityViewFilter,
    DateRangePicker,

  },
  name: 'PlannedServicesIndex',
  data() {
    return {
      plannedServices: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
      selectedPlannedService: null,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.$store.state.plannedServiceIndexState.dateRange;
      },
      set(value) {
        this.$store.commit('plannedServiceIndexState/setDateRange', value);
      },
    },
    filterText: {
      get() {
        return this.$store.state.plannedServiceIndexState.filterText;
      },
      set(value) {
        this.$store.commit('plannedServiceIndexState/setFilterText', value);
      },
    },
    filterDevice: {
      get() {
        return this.$store.state.plannedServiceIndexState.filterDeviceId;
      },
      set(value) {
        this.$store.commit('plannedServiceIndexState/setFilterDeviceId', value);
      },
    },
    tableOptions: {
      get() {
        return this.$store.state.plannedServiceIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('plannedServiceIndexState/setTableOptions', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'locationTop',
          text: i18n.t('plannedService.locationTop'),
          getValue: scheduledPlannedService => {
            return scheduledPlannedService.locationTop;
          },
        },
        {
          value: 'locationSub',
          text: i18n.t('plannedService.locationSub'),
          getValue: scheduledPlannedService => {
            return scheduledPlannedService.locationSub;
          },
        },
        {
          value: 'name',
          text: i18n.t('plannedService.name'),
          getValue: scheduledPlannedService => {
            return scheduledPlannedService.name;
          },
        },
        {
          value: 'device',
          text: i18n.t('plannedService.device'),
          getValue: scheduledPlannedService => {
            return this.$options.filters.device(scheduledPlannedService.device);
          },
        },
        {
          value: 'scheduleDate',
          text: i18n.t('plannedService.scheduleDate'),
          getValue: scheduledPlannedService => {
            return this.$options.filters.date(scheduledPlannedService.scheduleDate);
          },
        },
        {
          value: 'durationMinutes',
          text: i18n.t('plannedService.duration'),
          getValue: scheduledPlannedService => {
            return this.$options.filters.duration(scheduledPlannedService.durationMinutes);
          },
        },
        {
          value: 'serviceManagers',
          text: i18n.t('plannedService.serviceManagers'),
          sortable: false,
          getValue: scheduledPlannedService => {
            return scheduledPlannedService.serviceManagers.map(user => this.$options.filters.user(user));
          },
        },
        {
          value: 'authorUsers',
          text: i18n.t('plannedService.authorUsers'),
          sortable: false,
          getValue: scheduledPlannedService => {
            return scheduledPlannedService.authorUsers.map(user => this.$options.filters.user(user));
          },
        },
        {
          value: 'repeatCount', //count for sorting my count
          text: i18n.t('plannedService.repeatType'),
          getValue: scheduledPlannedService => {

            if (!scheduledPlannedService.repeatCount || !scheduledPlannedService.repeatType) {
              return i18n.t('plannedService.repeatTypeNone');
            }

            return scheduledPlannedService.repeatCount + ' ' +
                i18n.t('plannedService.repeatType' + scheduledPlannedService.repeatType);
          },
        },
        {
          value: 'serviceDoneDate',
          text: i18n.t('plannedService.serviceDoneDate'),
          getValue: scheduledPlannedService => {
            if (!scheduledPlannedService.serviceId) {
              return null;
            }
            return this.$options.filters.date(scheduledPlannedService.serviceDoneDate);
          },
        },
        {
          value: 'priority',
          text: i18n.t('plannedService.priority'),
          getValue: scheduledPlannedService => {
            return this.$options.filters.priority(scheduledPlannedService.priority);
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: scheduledPlannedService => {
            return this.createRowActions(scheduledPlannedService);
          },
        },
      ];
    },
    tableRows() {
      return this.plannedServices.map(scheduledPlannedService => {
        let row = {};
        row.scheduledPlannedService = scheduledPlannedService;
        row.routerLinkTo = `plannedservices/${scheduledPlannedService.id}`;

        row.rowClick = () => {
          this.$router.push(`plannedservices/${scheduledPlannedService.id}`);
        };

        this.tableHeaders.forEach(header => {
          if (header.getValue) {
            row[header.value] = header.getValue(scheduledPlannedService);
          }
        });
        return row;
      });
    },

    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.confirmPlannedServiceInstanceDelete(this.selectedRows);
          },
        },
      ];
      return actions.filter(action => action.hasPermission);
    },
  },
  methods: {
    createRowActions(scheduledPlannedService) {

      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: true,
          click: () => {
            this.$router.push(`plannedservices/${scheduledPlannedService.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.openEditDialog(scheduledPlannedService.id);
          },
        },
        {
          label: i18n.t('plannedService.moveToDateDialog.title'),
          icon: 'mdi-calendar-arrow-right',
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.moveToDate(scheduledPlannedService);
          },

        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          click: () => {
            this.confirmPlannedServiceInstanceDelete([{ scheduledPlannedService: scheduledPlannedService }]);
          },
        },
      ];

      if (scheduledPlannedService.repeatType) {
        actions.push(
            {
              label: i18n.t('plannedService.deleteRepeatingPlannedService'),
              icon: 'mdi-delete',
              hasPermission: this.$featurePermissions.plannedServiceCRUD,
              click: () => {
                this.confirmDeletePlannedService([{ scheduledPlannedService: scheduledPlannedService }]);
              },
            },
        );
      }

      if (scheduledPlannedService.serviceId == null) {
        let perFormService = {
          label: i18n.t('plannedService.performPlannedService'),
          icon: 'mdi-tools',
          hasPermission: this.$featurePermissions.plannedServiceCRUD,
          dividerAfter: true,
          click: () => {
            this.$refs.serviceDialog.openCreateServiceForPlannedServiceInstance(scheduledPlannedService.plannedServiceInstanceId);
          },
        };
        actions.unshift(perFormService);
      }

      return actions.filter(action => action.hasPermission);
    },
    confirmPlannedServiceInstanceDelete(items) {
      this.$refs.confirmDeletePlannedServiceInstanceDialog.show(items);
    },
    deletePlannedServiceInstances(items) {
      const plannedServiceInstanceIds = items.map(item => item.scheduledPlannedService.plannedServiceInstanceId);
      PlannedServiceService.deletePlannedServiceInstances(plannedServiceInstanceIds)
          .finally(() => {
            this.$refs.confirmDeletePlannedServiceInstanceDialog.close();
            this.fetch();
          });
    },

    confirmDeletePlannedService(items) {
      this.$refs.confirmDeletePlannedServiceDialog.show(items);
    },
    deletePlannedService(items) {
      const plannedServiceIds = items.map(item => item.scheduledPlannedService.id);
      PlannedServiceService.deletePlannedServices(plannedServiceIds)
          .finally(() => {
            this.$refs.confirmDeletePlannedServiceDialog.close();
            this.fetch();
          });
    },

    moveToDate(scheduledPlannedService) {
      this.$refs.moveToDateDialog.open(scheduledPlannedService.plannedServiceInstanceId, scheduledPlannedService.scheduleDate);
    },
    openEditDialog(plannedServiceId) {
      this.$refs.plannedServiceDialog.openEditForPlannedService(plannedServiceId);
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      PlannedServiceService.getPlannedServices({
            deviceId: this.filterDevice?.id,
            dateRangeStart: this.dateRange.startDate,
            dateRangeEnd: this.dateRange.endDate,
            filterText: this.filterText,
            limit: this.tableOptions.itemsPerPage,
            offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
            orderBy: this.tableOptions.sortBy[0],
            order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
          },
      )
          .then(result => {
            this.loading = false;
            this.plannedServices = result.data.items;
            this.totalCount = result.data.totalCount;
          }).catch(result => {
        console.log(JSON.stringify(result));
        this.loading = false;
      });
    },
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
